import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { foodItems as mockFoodItems } from "@/data/mockData";
import { type FoodItem } from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { Label } from "@/components/ui/label";
import { Plus, Search, Trash2, Edit, MoreHorizontal } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { foodItemApi } from "@/services/api";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const DietPlans = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("");
  
  const containsArabic = (text: string) => /[\u0600-\u06FF]/.test(text);
  const needsRTL = (text: string) => containsArabic(text) || (/^\d/.test(text) && containsArabic(text));
  
  const sanitizeFoodName = (name: string) => {
    return name.replace(/,/g, '-');
  };

  const [showAddFoodDrawer, setShowAddFoodDrawer] = useState(false);
  const [foodItems, setFoodItems] = useState<FoodItem[]>([]);
  const [loading, setLoading] = useState(true);
  
  // New food form state
  const [newFoodName, setNewFoodName] = useState("");
  const [newFoodCategory, setNewFoodCategory] = useState("");
  const [newFoodProtein, setNewFoodProtein] = useState("");
  const [newFoodCarbohydrate, setNewFoodCarbohydrate] = useState("");
  const [newFoodFat, setNewFoodFat] = useState("");
  const [newFoodEnergy, setNewFoodEnergy] = useState("");
  const [newFoodServingSize, setNewFoodServingSize] = useState("");
  const [newFoodDescription, setNewFoodDescription] = useState("");
  
  // Additional nutritional fields
  const [newFoodRefuse, setNewFoodRefuse] = useState("");
  const [newFoodWater, setNewFoodWater] = useState("");
  const [newFoodAsh, setNewFoodAsh] = useState("");
  const [newFoodFiber, setNewFoodFiber] = useState("");
  const [newFoodSodium, setNewFoodSodium] = useState("");
  const [newFoodPotassium, setNewFoodPotassium] = useState("");
  const [newFoodCalcium, setNewFoodCalcium] = useState("");
  const [newFoodPhosphorus, setNewFoodPhosphorus] = useState("");
  const [newFoodMagnesium, setNewFoodMagnesium] = useState("");
  const [newFoodIron, setNewFoodIron] = useState("");
  const [newFoodZinc, setNewFoodZinc] = useState("");
  const [newFoodCopper, setNewFoodCopper] = useState("");
  const [newFoodVitaminA, setNewFoodVitaminA] = useState("");
  const [newFoodVitaminC, setNewFoodVitaminC] = useState("");
  const [newFoodThiamin, setNewFoodThiamin] = useState("");
  const [newFoodRiboflavin, setNewFoodRiboflavin] = useState("");
  const [savingFood, setSavingFood] = useState(false);
  const [newFoodError, setNewFoodError] = useState<string | null>(null);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  
  // Edit food state
  const [editingFood, setEditingFood] = useState<FoodItem | null>(null);
  const [showEditFoodDrawer, setShowEditFoodDrawer] = useState(false);
  const [updatingFood, setUpdatingFood] = useState(false);
  const [editFoodError, setEditFoodError] = useState<string | null>(null);

  useEffect(() => {
    foodItemApi.getAll()
      .then(foods => {
        const mappedFoods = foods.map(food => ({
          _id: food._id,
          name: food.name,
          category: food.category,
          protein: food.protein,
          fat: food.fat,
          energy: food.energy,
          carbohydrate: food.carbohydrate,
          servingSize: food.servingSize || '',
          description: food.description || '',
          isCustom: food.isCustom || false,
          refuse: food.refuse || 0,
          water: food.water || 0,
          ash: food.ash || 0,
          fiber: food.fiber || 0,
          sodium: food.sodium || 0,
          potassium: food.potassium || 0,
          calcium: food.calcium || 0,
          phosphorus: food.phosphorus || 0,
          magnesium: food.magnesium || 0,
          iron: food.iron || 0,
          zinc: food.zinc || 0,
          copper: food.copper || 0,
          vitaminA: food.vitaminA || 0,
          vitaminC: food.vitaminC || 0,
          thiamin: food.thiamin || 0,
          riboflavin: food.riboflavin || 0
        }));
        setFoodItems(mappedFoods);
        setLoading(false);
      })
      .catch(() => {
        const convertedMockFoods = mockFoodItems.map(food => ({
          _id: food.id,
          name: food.name,
          category: food.category,
          protein: food.protein,
          fat: food.fat,
          energy: food.energy,
          carbohydrate: food.carbohydrate,
          servingSize: food.servingSize || '',
          description: '',
          isCustom: false,
          refuse: food.refuse || 0,
          water: food.water || 0,
          ash: food.ash || 0,
          fiber: food.fiber || 0,
          sodium: food.sodium || 0,
          potassium: food.potassium || 0,
          calcium: food.calcium || 0,
          phosphorus: food.phosphorus || 0,
          magnesium: food.magnesium || 0,
          iron: food.iron || 0,
          zinc: Number(food.zinc) || 0,
          copper: food.copper || 0,
          vitaminA: Number(food.vitaminA) || 0,
          vitaminC: Number(food.vitaminC) || 0,
          thiamin: food.thiamin || 0,
          riboflavin: food.riboflavin || 0
        }));
        setFoodItems(convertedMockFoods);
        setLoading(false);
      });
  }, []);

  const filteredFoods = foodItems.filter((food) => {
    const matchesSearch = food.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === "all" || !categoryFilter ? true : food.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const groupedFoods = filteredFoods.reduce((groups, food) => {
    const category = food.category;
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(food);
    return groups;
  }, {} as Record<string, FoodItem[]>);

  const categories = Array.from(new Set(foodItems.map((food) => food.category)));

  const handleCreateNewPlan = () => {
    navigate('/diet-editor');
  };

  const handleAddNewFood = () => {
    setShowAddFoodDrawer(true);
  };

  const handleEditFood = (food: FoodItem) => {
    setEditingFood(food);
    setShowEditFoodDrawer(true);
  };

  const handleUpdateFood = async () => {
    if (!editingFood) return;
    
    setEditFoodError(null);
    
    if (!editingFood.name || !editingFood.category || !editingFood.protein || !editingFood.carbohydrate || !editingFood.fat || !editingFood.energy || !editingFood.servingSize) {
      setEditFoodError("Please fill in all required fields.");
      return;
    }
    
    setUpdatingFood(true);
    try {
      const foodData = {
        name: sanitizeFoodName(editingFood.name),
        category: editingFood.category,
        protein: Number(editingFood.protein),
        carbohydrate: Number(editingFood.carbohydrate),
        fat: Number(editingFood.fat),
        energy: Number(editingFood.energy),
        servingSize: editingFood.servingSize,
        description: editingFood.description || undefined,
        isCustom: editingFood.isCustom,
        refuse: Number(editingFood.refuse) || 0,
        water: Number(editingFood.water) || 0,
        ash: Number(editingFood.ash) || 0,
        fiber: Number(editingFood.fiber) || 0,
        sodium: Number(editingFood.sodium) || 0,
        potassium: Number(editingFood.potassium) || 0,
        calcium: Number(editingFood.calcium) || 0,
        phosphorus: Number(editingFood.phosphorus) || 0,
        magnesium: Number(editingFood.magnesium) || 0,
        iron: Number(editingFood.iron) || 0,
        zinc: Number(editingFood.zinc) || 0,
        copper: Number(editingFood.copper) || 0,
        vitaminA: Number(editingFood.vitaminA) || 0,
        vitaminC: Number(editingFood.vitaminC) || 0,
        thiamin: Number(editingFood.thiamin) || 0,
        riboflavin: Number(editingFood.riboflavin) || 0
      };
      
      await foodItemApi.update(editingFood._id!, foodData);
      
      setFoodItems(prev => prev.map(food => 
        food._id === editingFood._id ? editingFood : food
      ));
      
      setShowEditFoodDrawer(false);
      setEditingFood(null);
      
      toast({
        title: "Food Updated",
        description: "Food item has been updated successfully.",
        variant: "default",
      });
    } catch (err: any) {
      setEditFoodError(err.message || "Failed to update food item.");
      toast({
        title: "Error",
        description: err.message || "Failed to update food item.",
        variant: "destructive",
      });
    } finally {
      setUpdatingFood(false);
    }
  };

  const handleDeleteFood = async (foodId: string) => {
    try {
      await foodItemApi.delete(foodId);
      
      setFoodItems(prev => prev.filter(food => food._id !== foodId));
      
      toast({
        title: "Food Deleted",
        description: "Food item has been deleted successfully.",
        variant: "default",
      });
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Failed to delete food item.",
        variant: "destructive",
      });
    }
  };

  const handleSaveFood = async () => {
    setNewFoodError(null);
    
    if (!newFoodName || !newFoodCategory || !newFoodProtein || !newFoodCarbohydrate || !newFoodFat || !newFoodEnergy || !newFoodServingSize) {
      setNewFoodError("Please fill in all required fields.");
      return;
    }
    
    setSavingFood(true);
    try {
      const foodData = {
        name: sanitizeFoodName(newFoodName),
        category: newFoodCategory,
        protein: parseFloat(newFoodProtein),
        carbohydrate: parseFloat(newFoodCarbohydrate),
        fat: parseFloat(newFoodFat),
        energy: parseFloat(newFoodEnergy),
        servingSize: newFoodServingSize,
        description: newFoodDescription || undefined,
        isCustom: true,
        refuse: parseFloat(newFoodRefuse) || 0,
        water: parseFloat(newFoodWater) || 0,
        ash: parseFloat(newFoodAsh) || 0,
        fiber: parseFloat(newFoodFiber) || 0,
        sodium: parseFloat(newFoodSodium) || 0,
        potassium: parseFloat(newFoodPotassium) || 0,
        calcium: parseFloat(newFoodCalcium) || 0,
        phosphorus: parseFloat(newFoodPhosphorus) || 0,
        magnesium: parseFloat(newFoodMagnesium) || 0,
        iron: parseFloat(newFoodIron) || 0,
        zinc: parseFloat(newFoodZinc) || 0,
        copper: parseFloat(newFoodCopper) || 0,
        vitaminA: parseFloat(newFoodVitaminA) || 0,
        vitaminC: parseFloat(newFoodVitaminC) || 0,
        thiamin: parseFloat(newFoodThiamin) || 0,
        riboflavin: parseFloat(newFoodRiboflavin) || 0
      };
      
      const createdFood = await foodItemApi.create(foodData);
      
      const newFoodItem = {
        _id: createdFood._id,
        name: createdFood.name,
        category: createdFood.category,
        protein: createdFood.protein,
        fat: createdFood.fat,
        energy: createdFood.energy,
        carbohydrate: createdFood.carbohydrate,
        servingSize: createdFood.servingSize || '',
        description: createdFood.description || '',
        isCustom: createdFood.isCustom || false,
        refuse: createdFood.refuse || 0,
        water: createdFood.water || 0,
        ash: createdFood.ash || 0,
        fiber: createdFood.fiber || 0,
        sodium: createdFood.sodium || 0,
        potassium: createdFood.potassium || 0,
        calcium: createdFood.calcium || 0,
        phosphorus: createdFood.phosphorus || 0,
        magnesium: createdFood.magnesium || 0,
        iron: createdFood.iron || 0,
        zinc: createdFood.zinc || 0,
        copper: createdFood.copper || 0,
        vitaminA: createdFood.vitaminA || 0,
        vitaminC: createdFood.vitaminC || 0,
        thiamin: createdFood.thiamin || 0,
        riboflavin: createdFood.riboflavin || 0
      };
      setFoodItems(prev => [...prev, newFoodItem]);
      
      setShowAddFoodDrawer(false);
      
      // Reset form
      setNewFoodName("");
      setNewFoodCategory("");
      setNewFoodProtein("");
      setNewFoodCarbohydrate("");
      setNewFoodFat("");
      setNewFoodEnergy("");
      setNewFoodServingSize("");
      setNewFoodDescription("");
      setNewFoodRefuse("");
      setNewFoodWater("");
      setNewFoodAsh("");
      setNewFoodFiber("");
      setNewFoodSodium("");
      setNewFoodPotassium("");
      setNewFoodCalcium("");
      setNewFoodPhosphorus("");
      setNewFoodMagnesium("");
      setNewFoodIron("");
      setNewFoodZinc("");
      setNewFoodCopper("");
      setNewFoodVitaminA("");
      setNewFoodVitaminC("");
      setNewFoodThiamin("");
      setNewFoodRiboflavin("");
      
      toast({
        title: "Food Added",
        description: "New food item has been added to the database.",
        variant: "default",
      });
    } catch (err: any) {
      setNewFoodError(err.message || "Failed to add food item.");
      toast({
        title: "Error",
        description: err.message || "Failed to add food item.",
        variant: "destructive",
      });
    } finally {
      setSavingFood(false);
    }
  };

  return (
    <>
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Diet Plans</h1>
          <p className="text-muted-foreground">
            Browse foods and create diet plans for clients
          </p>
        </div>
        <Button onClick={handleCreateNewPlan}>
          <Plus className="mr-2 h-4 w-4" /> Create New Plan
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Food Database</CardTitle>
          <CardDescription>
            Browse all food items with detailed nutritional information
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search foods..."
                className="pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((category) => (
                  <SelectItem key={category} value={category}>
                    {category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-md border">
            <Table className="text-sm border-collapse">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[150px] border text-center">Food</TableHead>
                  <TableHead className="text-center h-[150px] border">
                    <div className="rotate-[-90deg] whitespace-nowrap">Refuse</div>
                  </TableHead>
                  <TableHead className="text-center h-[150px] border">
                    <div className="rotate-[-90deg] whitespace-nowrap">Water</div>
                  </TableHead>
                  <TableHead className="text-center h-[150px] border">
                    <div className="rotate-[-90deg] whitespace-nowrap">Energy</div>
                  </TableHead>
                  <TableHead className="text-center h-[150px] border">
                    <div className="rotate-[-90deg] whitespace-nowrap">Protein (g)</div>
                  </TableHead>
                  <TableHead className="text-center h-[150px] border">
                    <div className="rotate-[-90deg] whitespace-nowrap">Fat (g)</div>
                  </TableHead>
                  <TableHead className="text-center h-[150px] border">
                    <div className="rotate-[-90deg] whitespace-nowrap">Ash (g)</div>
                  </TableHead>
                  <TableHead className="text-center h-[150px] border">
                    <div className="rotate-[-90deg] whitespace-nowrap">Fiber (g)</div>
                  </TableHead>
                  <TableHead className="text-center h-[150px] border">
                    <div className="rotate-[-90deg] whitespace-nowrap">Carbs (g)</div>
                  </TableHead>
                  <TableHead className="text-center h-[150px] border">
                    <div className="rotate-[-90deg] whitespace-nowrap">Sodium</div>
                  </TableHead>
                  <TableHead className="text-center h-[150px] border">
                    <div className="rotate-[-90deg] whitespace-nowrap">Potassium</div>
                  </TableHead>
                  <TableHead className="text-center h-[150px] border">
                    <div className="rotate-[-90deg] whitespace-nowrap">Calcium</div>
                  </TableHead>
                  <TableHead className="text-center h-[150px] border">
                    <div className="rotate-[-90deg] whitespace-nowrap">Phosphorus</div>
                  </TableHead>
                  <TableHead className="text-center h-[150px] border">
                    <div className="rotate-[-90deg] whitespace-nowrap">Magnesium</div>
                  </TableHead>
                  <TableHead className="text-center h-[150px] border">
                    <div className="rotate-[-90deg] whitespace-nowrap">Iron</div>
                  </TableHead>
                  <TableHead className="text-center h-[150px] border">
                    <div className="rotate-[-90deg] whitespace-nowrap">Zinc</div>
                  </TableHead>
                  <TableHead className="text-center h-[150px] border">
                    <div className="rotate-[-90deg] whitespace-nowrap">Copper</div>
                  </TableHead>
                  <TableHead className="text-center h-[150px] border">
                    <div className="rotate-[-90deg] whitespace-nowrap">Vitamin A</div>
                  </TableHead>
                  <TableHead className="text-center h-[150px] border">
                    <div className="rotate-[-90deg] whitespace-nowrap">Vitamin C</div>
                  </TableHead>
                  <TableHead className="text-center h-[150px] border">
                    <div className="rotate-[-90deg] whitespace-nowrap">Thiamin</div>
                  </TableHead>
                  <TableHead className="text-center h-[150px] border">
                    <div className="rotate-[-90deg] whitespace-nowrap">Riboflavin</div>
                  </TableHead>
                  <TableHead className="text-center h-[150px] border">
                    <div className="rotate-[-90deg] whitespace-nowrap">Serving Size</div>
                  </TableHead>
                  <TableHead className="text-center h-[150px] border">
                    <div className="rotate-[-90deg] whitespace-nowrap">Actions</div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={24} className="text-center py-4 border">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : filteredFoods.length > 0 ? (
                  Object.entries(groupedFoods).map(([category, foods]) => (
                    <React.Fragment key={`group-${category}`}>
                      <TableRow className="bg-green-50 hover:bg-green-100">
                        <TableCell colSpan={24} className="font-semibold py-1 text-green-800 border">
                          {category}
                        </TableCell>
                      </TableRow>
                      {foods.map((food) => (
                        <TableRow key={food._id} className="hover:bg-muted/50">
                          <TableCell 
                            className="font-medium py-1 border text-left"
                            dir={needsRTL(food.name) ? 'rtl' : undefined}
                            style={needsRTL(food.name) ? { unicodeBidi: 'plaintext' as any } : undefined}
                          >
                            {food.name}
                          </TableCell>
                          <TableCell className="text-center py-1 border">{food.refuse || '-'}</TableCell>
                          <TableCell className="text-center py-1 border">{food.water || '-'}</TableCell>
                          <TableCell className="text-center py-1 border">{food.energy || '-'}</TableCell>
                          <TableCell className="text-center py-1 border">{food.protein || '-'}</TableCell>
                          <TableCell className="text-center py-1 border">{food.fat || '-'}</TableCell>
                          <TableCell className="text-center py-1 border">{food.ash || '-'}</TableCell>
                          <TableCell className="text-center py-1 border">{food.fiber || '-'}</TableCell>
                          <TableCell className="text-center py-1 border">{food.carbohydrate || '-'}</TableCell>
                          <TableCell className="text-center py-1 border">{food.sodium || '-'}</TableCell>
                          <TableCell className="text-center py-1 border">{food.potassium || '-'}</TableCell>
                          <TableCell className="text-center py-1 border">{food.calcium || '-'}</TableCell>
                          <TableCell className="text-center py-1 border">{food.phosphorus || '-'}</TableCell>
                          <TableCell className="text-center py-1 border">{food.magnesium || '-'}</TableCell>
                          <TableCell className="text-center py-1 border">{food.iron || '-'}</TableCell>
                          <TableCell className="text-center py-1 border">{food.zinc || '-'}</TableCell>
                          <TableCell className="text-center py-1 border">{food.copper || '-'}</TableCell>
                          <TableCell className="text-center py-1 border">{food.vitaminA || '-'}</TableCell>
                          <TableCell className="text-center py-1 border">{food.vitaminC || '-'}</TableCell>
                          <TableCell className="text-center py-1 border">{food.thiamin || '-'}</TableCell>
                          <TableCell className="text-center py-1 border">{food.riboflavin || '-'}</TableCell>
                          <TableCell className="text-center py-1 border">{food.servingSize || '-'}</TableCell>
                          <TableCell className="text-center py-1 border">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleEditFood(food)}>
                                  <Edit className="mr-2 h-4 w-4" />
                                  Edit Food
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  onClick={() => handleDeleteFood(food._id || '')}
                                  className="text-red-600 focus:text-red-600"
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Delete Food
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={24} className="text-center py-4 text-muted-foreground border">
                      No foods found matching your search criteria
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>

        <CardFooter className="flex justify-between">
          <Button variant="outline" onClick={handleAddNewFood}>
            <Plus className="mr-2 h-4 w-4" /> Add New Food
          </Button>
          <Button onClick={handleCreateNewPlan}>Build Diet Plan</Button>
        </CardFooter>
      </Card>
    </div>

    <Drawer open={showAddFoodDrawer} onOpenChange={setShowAddFoodDrawer}>
      <DrawerContent>
        <DrawerHeader className="text-left">
          <DrawerTitle>Add New Food</DrawerTitle>
          <DrawerDescription>
            Add a new food item to the database
          </DrawerDescription>
        </DrawerHeader>
        <div className="px-4 py-2 max-h-[70vh] overflow-y-auto">
          <div className="grid gap-4 py-4">
            {newFoodError && (
              <div className="col-span-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-red-600 text-sm">{newFoodError}</p>
              </div>
            )}
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-right">
                Name *
              </Label>
              <Input
                id="name"
                className="col-span-3"
                placeholder="Food name"
                value={newFoodName}
                onChange={(e) => setNewFoodName(e.target.value)}
              />
            </div>
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="category" className="text-right">
                Category *
              </Label>
              {showNewCategoryInput ? (
                <div className="col-span-3 flex gap-2">
                  <Input
                    placeholder="Enter new category name"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                  />
                  <Button
                    size="sm"
                    onClick={() => {
                      if (newCategoryName.trim()) {
                        setNewFoodCategory(newCategoryName.trim());
                        setShowNewCategoryInput(false);
                        setNewCategoryName("");
                      }
                    }}
                  >
                    Add
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setShowNewCategoryInput(false);
                      setNewCategoryName("");
                      setNewFoodCategory("");
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <Select value={newFoodCategory} onValueChange={(value) => {
                  if (value === "new") {
                    setShowNewCategoryInput(true);
                  } else {
                    setNewFoodCategory(value);
                  }
                }}>
                  <SelectTrigger className="col-span-3">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((category) => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    ))}
                    <SelectItem value="new">+ Add New Category</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="energy" className="text-right">
                Energy (kcal) *
              </Label>
              <Input
                id="energy"
                type="number"
                className="col-span-3"
                placeholder="0"
                value={newFoodEnergy}
                onChange={(e) => setNewFoodEnergy(e.target.value)}
              />
            </div>
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="protein" className="text-right">
                Protein (g) *
              </Label>
              <Input
                id="protein"
                type="number"
                className="col-span-3"
                placeholder="0"
                value={newFoodProtein}
                onChange={(e) => setNewFoodProtein(e.target.value)}
              />
            </div>
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="carbohydrate" className="text-right">
                Carbohydrate (g) *
              </Label>
              <Input
                id="carbohydrate"
                type="number"
                className="col-span-3"
                placeholder="0"
                value={newFoodCarbohydrate}
                onChange={(e) => setNewFoodCarbohydrate(e.target.value)}
              />
            </div>
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="fat" className="text-right">
                Fat (g) *
              </Label>
              <Input
                id="fat"
                type="number"
                className="col-span-3"
                placeholder="0"
                value={newFoodFat}
                onChange={(e) => setNewFoodFat(e.target.value)}
              />
            </div>
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="servingSize" className="text-right">
                Serving Size *
              </Label>
              <Input
                id="servingSize"
                className="col-span-3"
                placeholder="e.g., 100g, 1 cup, 1 piece"
                value={newFoodServingSize}
                onChange={(e) => setNewFoodServingSize(e.target.value)}
              />
            </div>
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="description" className="text-right">
                Description
              </Label>
              <Input
                id="description"
                className="col-span-3"
                placeholder="Optional description"
                value={newFoodDescription}
                onChange={(e) => setNewFoodDescription(e.target.value)}
              />
            </div>
            
            {/* Additional Nutritional Information */}
            <div className="col-span-4 mt-4">
              <h4 className="font-medium text-sm mb-3 text-gray-700">Additional Nutritional Information (Optional)</h4>
            </div>
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="refuse" className="text-right">
                Refuse (g)
              </Label>
              <Input
                id="refuse"
                type="number"
                className="col-span-3"
                placeholder="0"
                value={newFoodRefuse}
                onChange={(e) => setNewFoodRefuse(e.target.value)}
              />
            </div>
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="water" className="text-right">
                Water (g)
              </Label>
              <Input
                id="water"
                type="number"
                className="col-span-3"
                placeholder="0"
                value={newFoodWater}
                onChange={(e) => setNewFoodWater(e.target.value)}
              />
            </div>
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="ash" className="text-right">
                Ash (g)
              </Label>
              <Input
                id="ash"
                type="number"
                className="col-span-3"
                placeholder="0"
                value={newFoodAsh}
                onChange={(e) => setNewFoodAsh(e.target.value)}
              />
            </div>
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="fiber" className="text-right">
                Fiber (g)
              </Label>
              <Input
                id="fiber"
                type="number"
                className="col-span-3"
                placeholder="0"
                value={newFoodFiber}
                onChange={(e) => setNewFoodFiber(e.target.value)}
              />
            </div>
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="sodium" className="text-right">
                Sodium (mg)
              </Label>
              <Input
                id="sodium"
                type="number"
                className="col-span-3"
                placeholder="0"
                value={newFoodSodium}
                onChange={(e) => setNewFoodSodium(e.target.value)}
              />
            </div>
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="potassium" className="text-right">
                Potassium (mg)
              </Label>
              <Input
                id="potassium"
                type="number"
                className="col-span-3"
                placeholder="0"
                value={newFoodPotassium}
                onChange={(e) => setNewFoodPotassium(e.target.value)}
              />
            </div>
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="calcium" className="text-right">
                Calcium (mg)
              </Label>
              <Input
                id="calcium"
                type="number"
                className="col-span-3"
                placeholder="0"
                value={newFoodCalcium}
                onChange={(e) => setNewFoodCalcium(e.target.value)}
              />
            </div>
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="phosphorus" className="text-right">
                Phosphorus (mg)
              </Label>
              <Input
                id="phosphorus"
                type="number"
                className="col-span-3"
                placeholder="0"
                value={newFoodPhosphorus}
                onChange={(e) => setNewFoodPhosphorus(e.target.value)}
              />
            </div>
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="magnesium" className="text-right">
                Magnesium (mg)
              </Label>
              <Input
                id="magnesium"
                type="number"
                className="col-span-3"
                placeholder="0"
                value={newFoodMagnesium}
                onChange={(e) => setNewFoodMagnesium(e.target.value)}
              />
            </div>
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="iron" className="text-right">
                Iron (mg)
              </Label>
              <Input
                id="iron"
                type="number"
                className="col-span-3"
                placeholder="0"
                value={newFoodIron}
                onChange={(e) => setNewFoodIron(e.target.value)}
              />
            </div>
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="zinc" className="text-right">
                Zinc (mg)
              </Label>
              <Input
                id="zinc"
                type="number"
                className="col-span-3"
                placeholder="0"
                value={newFoodZinc}
                onChange={(e) => setNewFoodZinc(e.target.value)}
              />
            </div>
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="copper" className="text-right">
                Copper (mg)
              </Label>
              <Input
                id="copper"
                type="number"
                className="col-span-3"
                placeholder="0"
                value={newFoodCopper}
                onChange={(e) => setNewFoodCopper(e.target.value)}
              />
            </div>
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="vitaminA" className="text-right">
                Vitamin A (µg)
              </Label>
              <Input
                id="vitaminA"
                type="number"
                className="col-span-3"
                placeholder="0"
                value={newFoodVitaminA}
                onChange={(e) => setNewFoodVitaminA(e.target.value)}
              />
            </div>
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="vitaminC" className="text-right">
                Vitamin C (mg)
              </Label>
              <Input
                id="vitaminC"
                type="number"
                className="col-span-3"
                placeholder="0"
                value={newFoodVitaminC}
                onChange={(e) => setNewFoodVitaminC(e.target.value)}
              />
            </div>
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="thiamin" className="text-right">
                Thiamin (mg)
              </Label>
              <Input
                id="thiamin"
                type="number"
                className="col-span-3"
                placeholder="0"
                value={newFoodThiamin}
                onChange={(e) => setNewFoodThiamin(e.target.value)}
              />
            </div>
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="riboflavin" className="text-right">
                Riboflavin (mg)
              </Label>
              <Input
                id="riboflavin"
                type="number"
                className="col-span-3"
                placeholder="0"
                value={newFoodRiboflavin}
                onChange={(e) => setNewFoodRiboflavin(e.target.value)}
              />
            </div>
          </div>
        </div>
        <DrawerFooter className="pt-2">
          <DrawerClose asChild>
            <Button variant="outline" disabled={savingFood}>Cancel</Button>
          </DrawerClose>
          <Button onClick={handleSaveFood} disabled={savingFood}>
            {savingFood ? "Saving..." : "Save Food"}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>

    {/* Edit Food Drawer */}
    <Drawer open={showEditFoodDrawer} onOpenChange={setShowEditFoodDrawer}>
      <DrawerContent>
        <DrawerHeader className="text-left">
          <DrawerTitle>Edit Food Item</DrawerTitle>
          <DrawerDescription>
            Update the food item information
          </DrawerDescription>
        </DrawerHeader>
        <div className="px-4 py-2 max-h-[70vh] overflow-y-auto">
          <div className="grid gap-4 py-4">
            {editFoodError && (
              <div className="col-span-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-red-600 text-sm">{editFoodError}</p>
              </div>
            )}
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="edit-name" className="text-right">
                Name *
              </Label>
              <Input
                id="edit-name"
                className="col-span-3"
                placeholder="Food name"
                value={editingFood?.name || ''}
                onChange={(e) => setEditingFood(prev => prev ? { ...prev, name: e.target.value } : null)}
              />
            </div>
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="edit-category" className="text-right">
                Category *
              </Label>
              <Select 
                value={editingFood?.category || ''} 
                onValueChange={(value) => setEditingFood(prev => prev ? { ...prev, category: value } : null)}
              >
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="edit-energy" className="text-right">
                Energy (kcal) *
              </Label>
              <Input
                id="edit-energy"
                type="number"
                className="col-span-3"
                placeholder="0"
                value={editingFood?.energy || ''}
                onChange={(e) => setEditingFood(prev => prev ? { ...prev, energy: Number(e.target.value) } : null)}
              />
            </div>
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="edit-protein" className="text-right">
                Protein (g) *
              </Label>
              <Input
                id="edit-protein"
                type="number"
                className="col-span-3"
                placeholder="0"
                value={editingFood?.protein || ''}
                onChange={(e) => setEditingFood(prev => prev ? { ...prev, protein: Number(e.target.value) } : null)}
              />
            </div>
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="edit-carbohydrate" className="text-right">
                Carbohydrate (g) *
              </Label>
              <Input
                id="edit-carbohydrate"
                type="number"
                className="col-span-3"
                placeholder="0"
                value={editingFood?.carbohydrate || ''}
                onChange={(e) => setEditingFood(prev => prev ? { ...prev, carbohydrate: Number(e.target.value) } : null)}
              />
            </div>
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="edit-fat" className="text-right">
                Fat (g) *
              </Label>
              <Input
                id="edit-fat"
                type="number"
                className="col-span-3"
                placeholder="0"
                value={editingFood?.fat || ''}
                onChange={(e) => setEditingFood(prev => prev ? { ...prev, fat: Number(e.target.value) } : null)}
              />
            </div>
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="edit-servingSize" className="text-right">
                Serving Size *
              </Label>
              <Input
                id="edit-servingSize"
                className="col-span-3"
                placeholder="e.g., 100g, 1 cup, 1 piece"
                value={editingFood?.servingSize || ''}
                onChange={(e) => setEditingFood(prev => prev ? { ...prev, servingSize: e.target.value } : null)}
              />
            </div>
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="edit-description" className="text-right">
                Description
              </Label>
              <Input
                id="edit-description"
                className="col-span-3"
                placeholder="Optional description"
                value={editingFood?.description || ''}
                onChange={(e) => setEditingFood(prev => prev ? { ...prev, description: e.target.value } : null)}
              />
            </div>
          </div>
        </div>
        <DrawerFooter className="pt-2">
          <Button variant="outline" onClick={() => setShowEditFoodDrawer(false)} disabled={updatingFood}>
            Cancel
          </Button>
          <Button onClick={handleUpdateFood} disabled={updatingFood}>
            {updatingFood ? "Updating..." : "Update Food"}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
    </>
  );
};

export default DietPlans;
